import { createPOIListImage } from "~/ui/poilistimage";
import { node, icon, fragment, separateWithElement } from "~/util/dom";
import { makeEvent, addListener, removeListeners } from "~/lib/signals";
import Tooltip, { ETooltipFlow } from "~/ui/tooltip";
import { CONFIG } from "~/config";
import { firmStats, getBadgeComposedComponent, isFirmFromPoi, isTwin, reviewGetBadgeColor, reviewHasBadge, reviewHasColoredBadge } from "~/util/review";
import { _ } from "~/i18n";
import { IPoi, SearchResult } from "~/interfaces";
//import { createKeyValTags } from "~/ui/keyvaltags";
import { Visualizer } from ".";
import createSDSIcon from "~/ui/icon";
import { ICON_INFO_OUTLINE_16 } from "~/ui/icons";
import { createCommercialCollaborationLink } from "~/ui/commercialCollaboration";
import { store } from "~/state/store";

type TBestReview = IPoi["review"]["best_review"];

function createFuel(payload: IPoi["extend"]["fuel"]) {
	// 1 nafta, 2 benzin, 3 lpg, 4 cng
	const data = payload.value;
	const fuelElement = node("div", { className: "fuel" });

	for (let idx = 0; idx < data.length && idx < 2; idx++) {
		if ([1, 2, 3, 4].indexOf(data[idx].id) === -1) {
			continue;
		}

		const fuelSpan = node("span");

		fuelSpan.setAttribute("itemscope", "itemscope");
		fuelSpan.setAttribute("itemtype", "http://schema.org/Product");

		const priceSpan = node("span");

		priceSpan.setAttribute("itemprop", "offers");
		priceSpan.setAttribute("itemscope", "itemscope");
		priceSpan.setAttribute("itemtype", "http://schema.org/Offer");

		const span = node("span", { innerHTML: data[idx].price, className: `type-${data[idx].id}` });
		const priceValue = data[idx].priceValue || "";

		span.setAttribute("itemprop", "price");
		span.setAttribute("content", priceValue);

		const name = node("meta", { content: data[idx].name });

		name.setAttribute("itemprop", "name");

		const currency = node("meta", { content: data[idx].currency });

		currency.setAttribute("itemprop", "priceCurrency");


		priceSpan.appendChild(span);
		priceSpan.appendChild(currency);

		fuelSpan.appendChild(name);
		fuelSpan.appendChild(priceSpan);

		fuelElement.appendChild(fuelSpan);
	}

	return fuelElement;
}

/**
 * @class Jedna polozka vysledku hledani v pravem sloupci
 */
export default class Item2 {
	_owner: Visualizer;

	_data: IPoi & SearchResult;

	_bestReview: null | TBestReview;

	_dom: {
		container: HTMLLIElement;
		box: HTMLDivElement;
		liInner: HTMLAnchorElement;
	};

	_isFirm: boolean;

	_paidFirm: boolean;

	_isTwin: boolean;

	_reported: Array<"phone" | "link" | "detail" | "show">;

	_es: Array<string>;

	_correction: any;

	_tooltipClear: () => void;

	_sponsoredTooltipClear: () => void;

	constructor(owner: Visualizer, poidata: IPoi & SearchResult, index: number, correction: any, bestReview: TBestReview) {
		this._data = poidata;
		this._owner = owner;
		this._es = [];
		this._reported = [];
		this._bestReview = bestReview;

		this._dom = {
			container: node("li", { className: "ui-search-item" }),
			box: node("div", { className: "content" }),
			liInner: node("a", { className: "li-inner", href: `${CONFIG.mapyURL}/?source=${this._data.source}&id=${this._data.id}` }),
		};
		this._correction = correction;
		this._tooltipClear = null;
		this._sponsoredTooltipClear = null;

		// placena & neplacena firma
		this._isFirm = isFirmFromPoi(this._data);
		this._paidFirm = this._isFirm && this._data.extendedData && this._data.extendedData.paid;
		this._isTwin = isTwin(this._data);

		this._build();
	}

	destroy() {
		this._dom.container.removeEventListener("mouseover", this, false);
		this._dom.container.removeEventListener("mouseout", this, false);
		this._dom.container.removeEventListener("mouseenter", this, false);
		this._dom.container.removeEventListener("mouseleave", this, false);
		this._dom.container.removeEventListener("click", this, false);
		if ("onauxclick" in document.body) {
			this._dom.container.removeEventListener("auxclick", this, false);
		} else {
			this._dom.container.removeEventListener("mousedown", this, false);
		}

		this._reported = [];

		if (this._paidFirm) {
			const paidFirmPhone = this._dom.container.querySelector("span.phone");
			const paidFirmLink = this._dom.container.querySelector("span.www");

			if (paidFirmPhone) {
				paidFirmPhone.removeEventListener("click", this, false);
			}

			if (paidFirmLink) {
				paidFirmLink.removeEventListener("click", this, false);
			}
		}

		if (this._tooltipClear) {
			Mapy.getComponent("layout").unsubscribe("scroll", this._tooltipClear);
			this._tooltipClear();
		}
		this._tooltipClear = null;

		if (this._sponsoredTooltipClear) {
			Mapy.getComponent("layout").unsubscribe("scroll", this._sponsoredTooltipClear);
			this._sponsoredTooltipClear();
		}
		this._sponsoredTooltipClear = null;

		removeListeners(this._es);
	}

	getContainer() {
		return this._dom.container;
	}

	getData() {
		return this._data;
	}

	isReported() {
		return this._reported.indexOf("show") > -1;
	}

	sendReport(ind, page = 0, origin = "") {
		if (this._reported.indexOf("show") < 0) {
			const state = store.getState();
			const queryId = state.search.queryId || "";
			const logObj = {
				showRating: this._data.review && reviewHasColoredBadge(this._data.review) ? 1 : 0,
				reviewCountWeighted: 0,
				reviewCount: this._data.review ? this._data.review.total : 0,
				index: ind,
				showReviews: this._data.review && reviewHasBadge(this._data.review) ? 1 : 0,
				// eslint-disable-next-line camelcase
				...origin ? { search_origin: origin } : {},
				// eslint-disable-next-line camelcase
				page_no: page,
				// eslint-disable-next-line camelcase
				query_id: queryId || "",
			};

			if (this._data.review) {
				logObj.reviewCountWeighted = parseFloat((this._data.review.review_weight || 0).toFixed(3));
			}

			if (this._isFirm && !this._isTwin) {
				firmStats("firmy-vizitka-doruceni", this._data, { placeId: 2, ...logObj });
			} else {
				// nefiremni
				Mapy.getComponent("logger").log("search_result_hit", {
					source: this._data.source,
					id: this._data.id,
					typeid: this._data.typeId || -1,
					firmTwin: this._isTwin ? 1 : 0,
					possibleRating: this._data.review ? 1 : 0,
					...logObj,
				});
			}

			this._reported.push("show");
		}
	}

	setState(newState) {
		this._dom.container.classList[newState ? "add" : "remove"]("hover");

		if (CONFIG.wasmEnabled) {
			const marker = Mapy.getComponent("search").getMarkers()?.getWasmMarker(this._data.source, this._data.id);
			const markerPoi = marker?.data?.customData?.poi;

			if (markerPoi && markerPoi.id === this._data.id && markerPoi.source === this._data.source) {
				marker.update({
					bringToTheFront: newState,
				});
			}
		}
	}

	handleEvent(evnt) {
		switch (evnt.type) {
			case "auxclick":
			case "mousedown":
				if (evnt.button !== 1) {
					break;
				}
			// eslint-disable-next-line no-fallthrough
			case "click":
				if (evnt.target.classList.contains("search-item__commercialCollaboration") || (evnt.target as HTMLElement).closest(".search-item__commercialCollaboration")) {
					return;
				}

				if (evnt.target.classList.contains("booking-url")) {
					const isBooking = this._data.extend.poi.booking.url.indexOf("https://www.booking.com/") === 0;
					const bookingLog = {
						id: this._data.id,
						source: this._data.source,
						name: this._data.title,
						typeId: 2,
						placeId: 2,
						target: isBooking ? "booking" : "custom",
					};

					Mapy.getComponent("logger").log("clickBooking", bookingLog);
					// pouze pro ceske veci
					if (this._isFirm) {
						firmStats("firmy-vizitka-akce", this._data, {
							placeId: 2,
							typeId: isBooking ? 28: 29,
						});
					}
				}

				evnt.stopPropagation();

				// pro booking chceme jit na url bookingu, ne detail
				if (evnt.target.classList.contains("booking-url")) {
					//
				} else if (evnt.target.classList.contains("phone")) {
					if (evnt.target.nodeName.toLowerCase() === "span") {
						if (!Mapy.Util.linkToNewWindow(evnt)) {
							evnt.preventDefault();
						}

						this._click(evnt, true);
					}

					// dle https://youtrack.seznam.net/issue/M-12121#focus=Comments-107-597763.0-0 to v pripade kliku na neaktivni i aktivni cislo ma logovat 6 a ne 7 v typeId, prestoze se otevira v pripade neaktivniho detail
					if (this._reported.indexOf("phone") === -1) {
						firmStats("firmy-vizitka-akce", this._data, { placeId: 2, typeId: 6 }); //firmy-vizitka-doruceni
						this._reported.push("phone");
					}
				} else if (evnt.target.classList.contains("www")) {
					if (this._reported.indexOf("link") === -1) {
						firmStats("firmy-vizitka-akce", this._data, { placeId: 2, typeId: 1 });//firmy-vizitka-doruceni
						this._reported.push("link");
					}
				} else {
					if (!Mapy.Util.linkToNewWindow(evnt)) {
						evnt.preventDefault();
					}

					this._click(evnt);

					if (this._reported.indexOf("detail") === -1) {
						Mapy.Util.firmStats("firmy-vizitka-akce", this._data, { placeId: 2, typeId: 7 });
						this._reported.push("detail");
					}
				}

				break;

			case "mouseover":
				this._mouseover(evnt);
				break;

			case "mouseout":
				this._mouseout(evnt);
				break;

			case "mouseenter":
				this._mouseenter();
				break;

			case "mouseleave":
				this._mouseleave();
				break;
			default:
		}
	}

	_build() {
		this.setState(false);
		const image = createPOIListImage(this._data, this._paidFirm);
		const row = node("div");
		const titleContainer = node("div", { className: "search-item__titleContainer" });

		this._dom.liInner.setAttribute("data-log-ignore", "1");
		row.append(this._dom.box, image);
		this._dom.liInner.append(row);
		this._dom.container.appendChild(this._dom.liInner);

		// nazev
		titleContainer.appendChild(node("h3", { innerHTML: this._data.multiTitle && this._data.multiTitle.length > 0 && this._data.multiTitle[0] !== "" ? this._data.multiTitle[0] : this._data.title }));
		this._dom.box.appendChild(titleContainer);

		// sponzorovano
		if (this._data.isAdvert) {
			const infoIcon = createSDSIcon({ symbol: ICON_INFO_OUTLINE_16 });
			const ccLinkPopup = createCommercialCollaborationLink();
			const ccLinkIcon = createCommercialCollaborationLink();
			const sponsoredTooltip = new Tooltip({
				anchor: infoIcon,
				trigger: infoIcon,
				content: ccLinkPopup,
				arrow: true,
				flow: ETooltipFlow.END,
				margin: [0, -10, 0, 0],
				hideTimeout: 100,
			});

			ccLinkIcon.classList.add("search-item__commercialCollaboration");
			ccLinkIcon.appendChild(infoIcon);
			ccLinkPopup.innerText = _("commercialCollaboration");
			titleContainer.appendChild(ccLinkIcon);
			this._sponsoredTooltipClear = () => sponsoredTooltip.hide();
			Mapy.getComponent("layout").subscribe("scroll", this._sponsoredTooltipClear);
		}

		// hodnoceni - 3 vazeny prumer
		if (this._data.review && reviewHasBadge(this._data.review)) {
			const badge = node(getBadgeComposedComponent(), {
				type: reviewGetBadgeColor(this._data.review),
				size: "small",
				rating: this._data.review.review_rating_stars,
				text: this._data.review.review_rating_caption,
				reviews: this._data.review.total,
				lang: CONFIG.defaultLang,
			});

			this._dom.box.appendChild(badge);
		}

		// typ a adresa
		const typeAddressParts = [];
		const addr = [].concat(this._data.multiTitle ? this._data.multiTitle.slice(1) : this._data.address).filter(addressRow => addressRow !== "");

		if (this._data.typeName && (!this._paidFirm || ["base", "firm"].indexOf(this._data.source) !== -1)) {
			typeAddressParts.push(node("span", { innerHTML: this._data.typeName }));
		}
		if (addr.length) {
			typeAddressParts.push(node("span", { innerHTML: addr.join(", ") }));
		}
		if (typeAddressParts.length) {
			const typeAddressParagraph = node("p");

			typeAddressParagraph.append(...separateWithElement(typeAddressParts, node("span", { innerHTML: " &bull; " })));
			this._dom.box.appendChild(typeAddressParagraph);
		}

		// cena pohonnych hmot
		if (this._data.extend?.fuel) {
			this._dom.box.appendChild(createFuel(this._data.extend.fuel));
		}

		// oteviraci doba, web a telefon
		const openingContactParts = [];
		const openingContactParagraph = node("p");

		// oteviraci doba
		if (this._data.extend?.opening) {
			const { holiday, msg } = this._data.extend.opening;

			if ((msg && this._data.extend.opening.seasons.length) || !msg) {
				const actualState = this._data.extend.opening.actualState;
				const actualStateCaption = this._data.extend.opening.actualStateCaption || "";
				const nowOpen = node("span", { className: "opening", innerHTML: this._data.extend.opening.actual });
				const openingParts = [];
				const openingFragment = fragment();

				if (msg || holiday) {
					const trigger = node("span", { className: "holiday", innerHTML: "*" });
					const tooltip = new Tooltip({
						anchor: openingContactParagraph,
						trigger: nowOpen,
						content: node("p", { innerHTML: (msg || "") + (msg && holiday ? "<br>" : "") + (holiday || "") }),
						arrow: false,
						flow: ETooltipFlow.START,
						offset: [8, 0, 8, 0],
						className: "search-form__tooltip",
						hideTimeout: 0,
					});

					this._tooltipClear = () => tooltip.hide();
					Mapy.getComponent("layout").subscribe("scroll", this._tooltipClear);
					nowOpen.appendChild(trigger);
				}

				if (actualState) {
					openingParts.push(node("span", { className: `opening-state ${actualState.replace(/[A-Z]/u, match => "-".concat(match.toLowerCase()))}`, innerHTML: actualStateCaption }));
				}

				openingParts.push(nowOpen);
				openingFragment.append(...separateWithElement(openingParts, node("span", { innerHTML: " &ndash; " })));
				openingContactParts.push(openingFragment);
			}
		}
		if (this._paidFirm && this._data.extend?.contact) {
			const contact = this._data.extend?.contact;

			// web
			if (contact.url || ("www" in contact && contact.www.value.length)) {
				const linkParameters = contact.url ? { innerHTML: _("search.visualizer.link2"), href: contact.url } : { innerHTML: contact.www.value[0].text || _("search.visualizer.link2"), href: contact.www.value[0].url };
				const link = node("a", { className: "www noprint contact", target: "_blank", ...linkParameters });

				// zakazat otevreni detailu
				link.setAttribute("data-log-action", "firmy-companyUrl");
				link.setAttribute("data-log-id", this._data.id.toString());
				link.setAttribute("data-log-ispaid", "true");
				link.addEventListener("click", this, false);
				openingContactParts.push(link);
			}

			// telefon
			if (contact.phone?.value?.length) {
				let find = null;

				contact.phone.value.every(item => {
					if (item.type === "telefon") {
						find = item;

						return false;
					}

					return true;
				});

				if (!find) {
					find = contact.phone.value[0];
				}

				let phoneData = "";

				if (find.countrycode !== "+420") {
					phoneData = `${find.countrycode} `;
				}

				phoneData += find.number.replace(/\B(?=(\d{3})+(?!\d))/gu, "&nbsp;");
				const number = find.countrycode + find.number;
				const phone = Mapy.Util.isMobile() ? node("a", { innerHTML: phoneData, className: "contact", href: `tel:${number}` }) : node("span", { innerHTML: phoneData, className: "contact" });

				phone.addEventListener("click", this, false);
				openingContactParts.push(phone);
			}
		}
		if (openingContactParts.length) {
			openingContactParagraph.append(...separateWithElement(openingContactParts, node("span", { innerHTML: " &bull; " })));
			this._dom.box.appendChild(openingContactParagraph);
		}

		// odkaz na Booking
		if (this._data.extend?.poi?.booking) {
			const booking = node("p", { className: "booking" });
			const link = node("a", { innerHTML: _("search.visualizer.bookingLink2"), className: "booking-url noprint", target: "_blank", href: this._data.extend.poi.booking.url });

			link.setAttribute("data-log-action", "firmy-companyUrl");
			link.setAttribute("data-log-id", this._data.id.toString());
			link.setAttribute("data-log-ispaid", "true");
			link.setAttribute("rel", "nofollow");
			link.addEventListener("click", this, false);
			booking.appendChild(link);
			this._dom.box.appendChild(booking);
		}

		// info o tom, ze je POI neaktivni
		if (this._data.inactive) {
			const disabledEl = node("p", { className: "inactive", textContent: this._data.temporarilyClosed ? _("detail.content.closed.temporarilyClosed") : _("detail.content.closed.title") });

			this._dom.box.appendChild(disabledEl);
		}

		// debug - analyticky mod pro firemni hodnoceni
		if (Mapy.CUS.userweb && Mapy.CUS.userweb.firmDebug && this._data.review) {
			this._dom.box.appendChild(node("p", { innerHTML: `Vážený počet recenzí: <strong>${parseFloat(this._data.review.review_weight.toFixed(3))}</strong>`, style: "font-style: italic;" }));
			this._dom.box.appendChild(node("p", {
				innerHTML: `use rating: <strong>${(this._data.review.use_rating || 0).toString()}</strong>, `
					+ `use total: <strong>${this._data.review.use_total ? this._data.review.use_total.toString() : "neexistuje"}</strong>`,
				style: "font-style: italic;",
			}));

			if (!reviewHasBadge(this._data.review)) {
				this._dom.box.appendChild(node("p", { innerHTML: `Prům. hodnocení: <strong>${(Math.max(this._data.review.review_rating, 0) / 20).toFixed(2)}</strong>`, style: "font-style: italic;" }));
				this._dom.box.appendChild(node("p", { innerHTML: `Počet recenzí: <strong>${this._data.review.total}</strong>`, style: "font-style: italic;" }));
			}

			this._dom.box.appendChild(node("p", { innerHTML: `Kategorie pásmování: ${this._data.review?.review_tier || "none"}` }));
			this._dom.box.appendChild(node("p", { innerHTML: `Hodnota prahování: ${this._data.review?.review_tier_threshold || "none"}` }));
			this._dom.box.appendChild(node("p", { innerHTML: `Visual: ${this._data.review?.badge || "none"}` }));
		}

		// debug - kvalita profilu
		if (Mapy.CUS.search?.showProfileQuality && ("profileQuality" in this._data || "profile_quality" in this._data)) {
			this._dom.box.appendChild(node("p", { innerHTML: `Profile quality: ${this._data.profileQuality || "none"}` }));
		}

		this._dom.container.addEventListener("mouseover", this, false);
		this._dom.container.addEventListener("mouseout", this, false);
		this._dom.container.addEventListener("mouseenter", this, false);
		this._dom.container.addEventListener("mouseleave", this, false);
		this._dom.container.addEventListener("click", this, false);
		if ("onauxclick" in document.body) {
			this._dom.container.addEventListener("auxclick", this, false);
		} else {
			this._dom.container.addEventListener("mousedown", this, false);
		}
		this._es.push(addListener(this, "search-marker-over", "_markOver"));
		this._es.push(addListener(this, "search-marker-out", "_markOut"));

		this._setBottomContent();
	}

	_setBottomContent() {
		// todo - az to budou chtit
		/*if (this._data.extend?.poi?.keyval_tags && this._data.extend.poi.keyval_tags.length > 0) {
			this._dom.box.appendChild(createKeyValTags(this._data.extend.poi.keyval_tags, true));
		}*/

		const bottomContentParts = [];

		if (this._data.searchMetadata?.quote?.length) {
			const isFrom = node("p", { className: "is-from" });
			const iconHolder = node("span", { className: "icon-holder" });
			const typeIcon = icon(`on${this._data.searchMetadata.quoteId === "destination" ? "history" : this._data.searchMetadata.quoteId}`);
			const text = node("span");

			for (let idx = 0; idx < this._data.searchMetadata.quote.length; idx++) {
				const quote = this._data.searchMetadata.quote[idx];

				idx > 0 && text.append(" ");

				switch (quote.type) {
					case "bold":
						text.append(node("b", {}, quote.value));
						break;

					case "text":
						text.append(`${quote.value}`);
						break;
					default:
				}
			}

			iconHolder.append(typeIcon);
			isFrom.append(iconHolder, text);
			bottomContentParts.push(isFrom);
		}

		if (this._data.matchedKeyVal) {
			const searchQuery = Mapy.getComponent("search").getState().q || "";
			const isFrom = node("p", { className: "is-from" });
			const iconHolder = node("span", { className: "icon-holder" });
			const typeIcon = icon(`ontextdesc`);
			const text = node("span", { innerHTML: `<b>${Mapy.Util.escapeHTML(searchQuery)}</b> ${_("search.findInTextQuery")}` });

			iconHolder.append(typeIcon);
			isFrom.append(iconHolder, text);
			bottomContentParts.push(isFrom);
		}

		// pokud tam bude sekce navic - vlozi do divu oba, horni margin 16px, gap: 8px
		if (this._bestReview) {
			const bestReviewCont = node("div", { className: "best-review-cont" });
			const avatar = node("mapy-avatar", { className: "best-review-cont__avatar", size: 24, premium: this._bestReview.user.hasPremium ? "mapy" : "none" });

			avatar.appendChild(node("img", { src: this._bestReview.user.image_url, crossOrigin: "anonymous" }));
			bestReviewCont.append(avatar, node("p", { className: "best-review-cont__reviewText" }, this._bestReview.text));
			bottomContentParts.push(bestReviewCont);
		}

		if (bottomContentParts.length > 0) {
			const bottomContent = node("div", { className: "bottom-content" });

			bottomContent.append(...bottomContentParts);
			this._dom.liInner.append(bottomContent);
		}
	}

	_mouseover(evnt?) {
		if (evnt) {
			makeEvent("search-item-over", this, { source: this._data.source, id: this._data.id });
		}
	}

	_mouseout(evnt?) {
		if (evnt) {
			makeEvent("search-item-out", this, { source: this._data.source, id: this._data.id });
		}
	}

	_mouseenter() {
		this.setState(true);
		makeEvent("search-item-enter", this, { source: this._data.source, id: this._data.id });
	}

	_mouseleave() {
		this.setState(false);
		makeEvent("search-item-leave", this, { source: this._data.source, id: this._data.id });
	}

	_click(event, omitLog = false) {
		this.setState(false);
		makeEvent("item-search-click", this, { event });
		if (this._reported.indexOf("detail") === -1 && !omitLog) {
			firmStats("firmy-vizitka-akce", this._data, { placeId: 2, typeId: 7 });
			this._reported.push("detail");
		}
	}

	_markOver(payload) {
		const { data } = payload;

		if (data.id === this._data.id && data.source === this._data.source) {
			this._mouseover();
		}
	}

	_markOut(payload) {
		const { data } = payload;

		if (data.id === this._data.id && data.source === this._data.source) {
			this._mouseout();
		}
	}
}
